import { useTenderDeadline } from "../../../../hooks/useTenderDeadline";
import React, { useState, useRef, useEffect } from "react";
import cn from "classnames";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  AlarmClockPlusIcon,
  BanIcon,
  FileDigitIcon,
  FileDownIcon,
  RotateCcwClockIcon,
  Share2Icon,
  ShareIcon,
  Trash2Icon,
  Undo2Icon,
} from "lucide-react";
import style from "../OrderInfoClient.module.scss";
import MoreIcon from "../../../shared/icons/MoreIcon";
import EditIconNew from "../../../shared/icons/EditIconNew";
import PlusIcon from "../../../shared/icons/PlusIcon";
import CopyIconNew from "../../../shared/icons/CopyIconNew";
import { File, User } from "../../../../services";
import CancelApp from "../../../order/info/CancelApp";
import OrdersDelete from "../../../order/info/OrdersDelete";
import SendToMarketplaceModal from "../../order-info/components/SendToMarketplaceModal";
import ExtendTenderEndDate from "../../order-info/components/ExtendTenderEndDate/ExtendTenderEndDate";
import AddProjectNumberModal from "../../order-info/components/AddProjectNumberModal/AddProjectNumberModal";
import ReturnToTenderModal from "./ReturnToTenderModal";
import { useClickOutside } from "../../../../hooks/useClickOutside";
import RebidTenderModal from "./RebidTenderModal";
import EditRouteDatesModal from "./EditRouteDatesModal/EditRouteDatesModal";
import { getRailwayOrderPath, isRailwayOrder } from "../../../../pages/orders/utils/railwayOrder";

export const CLIENT_DATE_EDIT_STATUSES = ["assignedexecutor", "confirmed"];

const OrderInfoClientMoreMenu = ({
  item,
  onForceUpdate,
  setIsOpen,
  hasBids = false,
  onOrderDataChanged,
  onEditOrder,
}) => {
  const { isExpired: isTenderExpiredByServer } = useTenderDeadline(item?.tenderDateEnd, { inclusive: true });
  const { t, i18n } = useTranslation();
  const { user } = useSelector((state) => state);
  const navigate = useNavigate();
  const moreRef = useRef(null);
  const copyLinkRef = useRef(null);

  const [isOpenDropdowns, setIsOpenDropdowns] = useState(false);
  const [isCancel, setCancelModal] = useState(false);
  const [isDelete, setDelete] = useState(false);
  const [shareLink, setShareLink] = useState("");
  const [isShareDropdownOpen, setIsShareDropdownOpen] = useState(false);
  const [showConfirmMarketplaceModal, setShowConfirmMarketplaceModal] = useState(false);
  const [showExtendTenderModal, setShowExtendTenderModal] = useState(false);
  const [showAddProjectNumberModal, setShowAddProjectNumberModal] = useState(false);
  const [showReturnToTenderModal, setShowReturnToTenderModal] = useState(false);
  const [showRebidTenderModal, setShowRebidTenderModal] = useState(false);
  const [showEditRouteDatesModal, setShowEditRouteDatesModal] = useState(false);

  const isOwner = user?.companyId === item?.companyId;
  const isTenderExpired = item?.tenderDateEnd
    ? isTenderExpiredByServer
    : false;
  const shouldShowRebidButton =
    isOwner &&
    item?.status === "awaitingoffer" &&
    isTenderExpired &&
    hasBids;

  const shouldShowReturnToTenderButton = () =>
    isOwner &&
    item?.isTender &&
    item?.parentId == null &&
    ["confirmed", "assignedexecutor"].includes(item?.status);

  const handleReturnToTender = () => {
    setShowReturnToTenderModal(true);
    setIsOpenDropdowns(false);
  };

  const shouldShowButton = () =>
    ["published", "awaitingoffer", "assigned", "moderation", "awaiting_payment", "rejected", "canceled"].includes(
      item?.status,
    );

  const isDateOnlyEditStatus = CLIENT_DATE_EDIT_STATUSES.includes(item?.status);
  const shouldShowEditButton = () => shouldShowButton() || isDateOnlyEditStatus;

  const shouldShowWithdrawButton = () =>
    [
      "published",
      "awaitingoffer",
      "assigned",
      "assignedexecutor",
      "booked",
      "confirmed",
      "moderation",
      "awaiting_payment",
      "rejected",
    ].includes(item?.status);

  const editPath = isRailwayOrder(item) && user?.type === "Client"
      ? getRailwayOrderPath("edit", item.id)
      : item?.isComplexTender && user?.type === "Client"
      ? `/orders/complex-tender/edit/${item?.id}`
      : user?.type === "Client"
        ? `/orders/edit/${item?.id}`
        : `/expeditor/orders/edit/${item?.id}`;
  const copyPath = isRailwayOrder(item) && user?.type === "Client"
    ? getRailwayOrderPath("copy", item.id)
    : item?.isComplexTender && user?.type === "Client"
    ? `/orders/complex-tender/copy/${item?.id}`
    : user?.type === "Client"
      ? `/orders/copy/${item?.id}`
      : `/expeditor/orders/copy/${item?.id}`;

  const handleClickOutside = (event) => {
    if (moreRef.current && !moreRef.current.contains(event.target)) {
      setIsOpenDropdowns(false);
    }
  };

  const downloadOrderPdf = async () => {
    try {
      const response = await File.getOrderPdf(item.id, i18n.language);
      const url = window.URL.createObjectURL(new Blob([response.data], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `order_${item.number}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error(error);
      toast.error(t("orderInfoClient.moreMenu.pdfDownloadError"));
    }
    setIsOpenDropdowns(false);
  };

  const handleShareClick = async () => {
    if (shareLink) {
      setIsShareDropdownOpen(!isShareDropdownOpen);
    } else {
      try {
        const response = await User.guestLogin(item.id);
        const accessToken = response.data.accessToken;
        const link = `${window.location.origin}/order/share/${accessToken}`;
        setShareLink(link);
        setIsShareDropdownOpen(true);
      } catch (error) {
        console.error(error);
        toast.error(t("orderInfo.genericError"));
      }
    }
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(shareLink);
    toast.success(t("orderInfo.ordersInfoHeader.linkCopied"));
    setIsShareDropdownOpen(false);
  };

  const handleMarketplaceSuccess = () => {
    setShowConfirmMarketplaceModal(false);
    onOrderDataChanged?.();
  };

  useClickOutside(copyLinkRef, () => setIsShareDropdownOpen(false));

  useEffect(() => {
    if (isOpenDropdowns) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpenDropdowns]);

  useEffect(() => {
    setShareLink("");
  }, [item]);

  if (!item) return null;

  return (
    <div className={style.more_wrapper} ref={moreRef}>
      <MoreIcon
        width={24}
        height={24}
        onClick={() => setIsOpenDropdowns(!isOpenDropdowns)}
        style={{ cursor: "pointer" }}
      />

      <div className={cn(style.more_dropdown, { [style.more_dropdown_open]: isOpenDropdowns })}>
        {shouldShowEditButton() && isOwner && (
          <div
            className={style.more_dropdown_item}
            onClick={() => {
              if (isDateOnlyEditStatus) {
                setShowEditRouteDatesModal(true);
                setIsOpenDropdowns(false);
                return;
              }

              if (onEditOrder) {
                onEditOrder(editPath);
              } else {
                navigate(editPath);
              }
            }}
          >
            <EditIconNew width={18} height={18} />
            {t(
              isDateOnlyEditStatus
                ? "orderInfoClient.moreMenu.editOrderDate"
                : "orderInfoClient.moreMenu.editOrder",
            )}
          </div>
        )}

        {isOwner && (
          <div className={style.more_dropdown_item} onClick={() => navigate(copyPath)}>
            <PlusIcon width={18} height={18} fill="#6D7885" />
            {t("orderInfoClient.moreMenu.copyOrder")}
          </div>
        )}

        {isOwner && item?.status === "awaitingoffer" && (
          <div
            className={style.more_dropdown_item}
            onClick={() => {
              setShowExtendTenderModal(true);
              setIsOpenDropdowns(false);
            }}
          >
            <AlarmClockPlusIcon size={18} color="#6D7885" />
            {t("orderInfo.ordersInfoHeader.extendTenderAction")}
          </div>
        )}

        {shouldShowRebidButton && (
          <div
            className={style.more_dropdown_item}
            onClick={() => {
              setShowRebidTenderModal(true);
              setIsOpenDropdowns(false);
            }}
          >
            <RotateCcwClockIcon size={18} color="#6D7885" />
            {t("orderInfoClient.rebid.action")}
          </div>
        )}

        {isOwner && (
          <div className={style.more_dropdown_item} onClick={downloadOrderPdf}>
            <FileDownIcon size={18} color="#6D7885" />
            {t("orderInfo.ordersInfoHeader.generateOrderPdf")}
          </div>
        )}

        {isOwner && (
          <div
            className={style.more_dropdown_item}
            onClick={() => {
              setShowAddProjectNumberModal(true);
              setIsOpenDropdowns(false);
            }}
          >
            <FileDigitIcon size={18} color="#6D7885" />
            {t("orderInfo.ordersInfoHeader.addProjectNumber")}
          </div>
        )}

        {item?.status !== "completed" && (
          <div ref={copyLinkRef}>
            <div className={style.more_dropdown_item} onClick={handleShareClick}>
              <Share2Icon size={18} color="#6D7885" />
              {t("orderInfo.ordersInfoHeader.shareOrder")}
            </div>
            {shareLink && isShareDropdownOpen && (
              <div className={style.share_box}>
                <input type="text" value={shareLink} readOnly className={style.share_input} />
                <button className={style.share_copy_btn} onClick={handleCopyLink}>
                  <CopyIconNew />
                  <span>{t("orderInfo.ordersInfoHeader.copy")}</span>
                </button>
              </div>
            )}
          </div>
        )}

        {shouldShowReturnToTenderButton() && (
          <div className={style.more_dropdown_item} onClick={handleReturnToTender}>
            <Undo2Icon size={18} color="#6D7885" />
            {t(
              item?.assignmentMethod === "Auction"
                ? "orderInfoClient.moreMenu.returnToAuction"
                : "orderInfoClient.moreMenu.returnToTender",
            )}
          </div>
        )}

        {shouldShowWithdrawButton() && isOwner && (
          <div
            className={cn(style.more_dropdown_item, style.more_dropdown_item_danger)}
            onClick={() => setCancelModal(true)}
          >
            <BanIcon size={18} color="#E53935" />
            {t("orderInfo.ordersInfoHeader.withdrawOrderButton")}
          </div>
        )}

        {/* {shouldShowButton() && isOwner && (
          <div
            className={cn(style.more_dropdown_item, style.more_dropdown_item_danger)}
            onClick={() => setDelete(true)}
          >
            <Trash2Icon size={18} color="#E53935" />
            {t("orderInfo.ordersInfoHeader.deleteOrderButton")}
          </div>
        )} */}
      </div>

      {isCancel && (
        <CancelApp
          item={item}
          onClose={() => setCancelModal(false)}
          onUpdateData={() => onOrderDataChanged?.()}
        />
      )}
      {isDelete && (
        <OrdersDelete
          item={item}
          onClose={() => setDelete(false)}
          onUpdateData={() => onForceUpdate?.()}
          setIsOpen={setIsOpen}
        />
      )}
      {showReturnToTenderModal && (
        <ReturnToTenderModal
          item={item}
          onClose={() => setShowReturnToTenderModal(false)}
          onSuccess={() => onOrderDataChanged?.()}
        />
      )}

      <SendToMarketplaceModal
        isOpen={showConfirmMarketplaceModal}
        orderId={item.id}
        tenderDateEnd={item?.tenderDateEnd}
        onSuccess={handleMarketplaceSuccess}
        onCancel={() => setShowConfirmMarketplaceModal(false)}
      />

      <ExtendTenderEndDate
        isOpen={showExtendTenderModal}
        orderId={item.id}
        tenderDateEnd={item?.tenderDateEnd}
        assignmentMethod={item?.assignmentMethod}
        onSuccess={() => {
          setShowExtendTenderModal(false);
          onOrderDataChanged?.();
        }}
        onCancel={() => setShowExtendTenderModal(false)}
      />

      <AddProjectNumberModal
        isOpen={showAddProjectNumberModal}
        orderId={item.id}
        onSuccess={() => {
          setShowAddProjectNumberModal(false);
          onOrderDataChanged?.();
        }}
        onCancel={() => setShowAddProjectNumberModal(false)}
      />

      <RebidTenderModal
        isOpen={showRebidTenderModal}
        orderId={item.id}
        onSuccess={() => {
          setShowRebidTenderModal(false);
          onOrderDataChanged?.();
        }}
        onCancel={() => setShowRebidTenderModal(false)}
      />

      <EditRouteDatesModal
        isOpen={showEditRouteDatesModal}
        order={item}
        onSuccess={() => onOrderDataChanged?.()}
        onCancel={() => setShowEditRouteDatesModal(false)}
      />
    </div>
  );
};

export default OrderInfoClientMoreMenu;
